/**
 * The Play window: the environment's graph played one passage at a time (a story scene by
 * scene, a prototype screen by screen), as its `play` says. Before starting it lists where the
 * story may start; then it shows the passage's page and its choices, the way back, and the end.
 * "Export" saves the story as one HTML file that plays by itself, to share as a demo.
 */
import { For, Show, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { MarkdownView } from "../../shared/Markdown";
import { toast } from "../../shared/toast";
import { fileShown as shownFile, openFile } from "../../state/assets";

/** The window. */
export function PlayWindow(): JSX.Element {
  const { play, pages, kinds , assets, presets } = useApp();
  /** The words for the kinds played and leading on (`scenes`, `leads to`). */
  const playedKinds = () => (play.def()?.nodes ?? []).map((id) => kinds.available().nodes.find((k) => k.id === id)?.label ?? id).join(", ");
  const choiceKinds = () => (play.def()?.choices ?? []).map((id) => kinds.available().links.find((k) => k.id === id)?.label ?? id).join(", ");
  const exportFile = () => {
    if (play.exportFile()) toast("The story was saved as a page that plays by itself.");
    else toast("Nothing to play yet.");
  };
  return (
    <div class="play-window" id="play">
      <Show when={play.def()} fallback={<p class="muted">This environment is not played. Writing plays its scenes, UI/UX its screens.</p>}>
        <Show
          when={play.at()}
          fallback={
            <div class="play-start">
              <Show
                when={play.story()?.passages.size}
                fallback={
                  <p class="muted">
                    Nothing to play yet: add nodes of kind {playedKinds()}, write their pages, and link them with “{choiceKinds()}” (the link's label is the choice's words).
                  </p>
                }
              >
                <h3>Start at</h3>
                <ul class="play-starts">
                  <For each={play.story()?.starts ?? []}>
                    {(id) => (
                      <li>
                        <button type="button" class="btn" data-play-start={id} onClick={() => play.start(id)}>
                          ▶ {play.story()?.passages.get(id)?.name}
                        </button>
                      </li>
                    )}
                  </For>
                </ul>
                <p class="muted small">
                  {play.story()?.passages.size} passages. Each {playedKinds()} shows its page; its “{choiceKinds()}” links are the choices.
                </p>
                <button type="button" class="btn" id="play-export" onClick={exportFile}>
                  Export as a playable page
                </button>
              </Show>
            </div>
          }
        >
          {(at) => (
            <article class="play-passage">
              <header class="play-bar">
                <button type="button" class="btn" id="play-back" disabled={!play.canBack()} onClick={() => play.back()}>
                  ← Back
                </button>
                <button type="button" class="btn" id="play-stop" onClick={() => play.stop()}>
                  Start again
                </button>
                <button type="button" class="btn" id="play-page" title="Open this passage's page" onClick={() => pages.show(at().id)}>
                  Edit page
                </button>
                <button type="button" class="btn" id="play-export" onClick={exportFile}>
                  Export
                </button>
              </header>
              <h2 id="play-title">{at().name}</h2>
              <Show when={at().page} fallback={<p class="muted">This passage has no page yet.</p>}>
                <MarkdownView text={at().page} resolve={(name) => !!pages.resolve(name)} onLink={(name) => pages.follow(name)} file={(path) => shownFile(assets, path)} onFile={(path) => openFile(assets, presets, path)} />
              </Show>
              <Show when={at().choices.length} fallback={<p class="muted play-end" id="play-end">The end.</p>}>
                <div class="play-choices">
                  <For each={at().choices}>
                    {(c) => (
                      <button
                        type="button"
                        class="play-choice"
                        data-play-choice={c.to ?? ""}
                        disabled={c.to === null}
                        title={c.to === null ? "This choice leads nowhere yet: wire its pin to a scene" : undefined}
                        onClick={() => c.to !== null && play.choose(c.to)}
                      >
                        {c.label}
                      </button>
                    )}
                  </For>
                </div>
              </Show>
            </article>
          )}
        </Show>
      </Show>
    </div>
  );
}
